import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { LogIn, LogOut } from "lucide-react";

import LanguageSwitcher from "shared/components/LanguageSwitcher";

const HAAT_LOGO_URL =
  "https://cdn.prod.website-files.com/6829acf870093a50cc6ceb69/682b03419ead6e45d8f2c59e_logo%20(2).svg";

interface HeaderProps {
  onLogout?: () => void;
  logoutLabel?: string;
  /** Optional landing-page CTA shown after the hero scrolls out of view. */
  ctaLabel?: string;
  onCtaClick?: () => void;
  showCta?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  onLogout,
  logoutLabel,
  ctaLabel,
  onCtaClick,
  showCta = false,
}) => {
  const { t } = useTranslation();
  const appName = String(t("appName") ?? "Courier Connect");
  const label = logoutLabel ?? "";

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 bg-white">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center" dir="ltr">
          <img
            src={HAAT_LOGO_URL}
            alt={appName}
            className="h-8 w-auto"
            loading="eager"
            decoding="async"
          />
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {showCta && ctaLabel ? (
            <button
              type="button"
              onClick={onCtaClick}
              className="rounded-lg bg-[#bf0035] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#bf0035]/90 sm:px-4 sm:text-sm"
            >
              {ctaLabel}
            </button>
          ) : null}
          {onLogout ? (
            <button
              type="button"
              onClick={onLogout}
              aria-label={label}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 transition-colors hover:border-[#bf0035]/20 hover:bg-[#bf0035]/[0.04] hover:text-[#bf0035]"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ) : (
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 transition-colors hover:border-[#bf0035]/20 hover:bg-[#bf0035]/[0.04] hover:text-[#bf0035]"
            >
              <LogIn size={16} />
              <span className="hidden sm:inline">
                {String(t("header.statusCheck") ?? "Check status")}
              </span>
            </Link>
          )}
          <LanguageSwitcher variant="header" />
        </div>
      </nav>
    </header>
  );
};

export default Header;
